/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd. <legal@vecros.com>
 * Proprietary and confidential.
 **********************************************************************************/

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, InputBase, useTheme, Tooltip, Typography } from "@mui/material";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import StopRoundedIcon from "@mui/icons-material/StopRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { accentFor } from "../model/tokens";
import { useAssistant } from "../AssistantContext";
import { useAssistantRecords, mentionMatches } from "../../../../../hooks/useAssistantRecords";

/** Find an active "@query" ending at the cursor (no whitespace after the @). */
function detectMention(value, pos) {
  const upto = value.slice(0, pos);
  const at = upto.lastIndexOf("@");
  if (at < 0) return null;
  const between = upto.slice(at + 1);
  if (/\s/.test(between)) return null;
  return { at, query: between };
}

/**
 * Composer with @-mention record picker (HubSpot's "type @ to mention a record").
 * Mentioned devices/organizations are tracked as context chips and passed to send().
 */
export default function Composer() {
  const theme = useTheme();
  const accent = accentFor(theme);
  const { send, stop, streaming, consumeSeed, pendingSeed } = useAssistant();
  const records = useAssistantRecords();
  const [text, setText] = useState("");
  const [context, setContext] = useState([]);
  const [mention, setMention] = useState(null); // { at, query } | null
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);

  const results = useMemo(
    () => (mention ? mentionMatches(records, mention.query) : []),
    [mention, records]
  );

  useEffect(() => setActiveIdx(0), [mention]);

  // Prefill when opened from a Pulse / suggestion seed.
  useEffect(() => {
    if (pendingSeed) {
      const seed = consumeSeed();
      if (seed) {
        setText(seed);
        if (inputRef.current) inputRef.current.focus();
      }
    }
  }, [pendingSeed, consumeSeed]);

  const onChange = (e) => {
    const val = e.target.value;
    setText(val);
    const m = detectMention(val, e.target.selectionStart);
    if (m) {
      setMention(m);
      records.load();
    } else {
      setMention(null);
    }
  };

  const selectMention = (rec) => {
    const pos = inputRef.current?.selectionStart ?? text.length;
    const upto = text.slice(0, pos);
    const at = upto.lastIndexOf("@");
    const before = text.slice(0, at);
    const after = text.slice(pos);
    const inserted = `@${rec.label} `;
    setText(before + inserted + after);
    setContext((prev) => (prev.find((c) => c.id === rec.id) ? prev : [...prev, rec]));
    setMention(null);
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        const caret = (before + inserted).length;
        el.focus();
        el.setSelectionRange(caret, caret);
      }
    });
  };

  const removeChip = (id) => setContext((prev) => prev.filter((c) => c.id !== id));

  const submit = () => {
    const t = text.trim();
    if (!t || streaming) return;
    const ctx = context;
    setText("");
    setContext([]);
    setMention(null);
    send(t, ctx);
  };

  const onKeyDown = (e) => {
    if (mention && results.length) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        selectMention(results[activeIdx] || results[0]);
        return;
      }
      if (e.key === "Escape") {
        setMention(null);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = text.trim().length > 0;
  const showMenu = Boolean(mention) && (results.length > 0 || !records.loaded);

  return (
    <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.border.color}`, position: "relative" }}>
      {/* @-mention dropdown */}
      {showMenu && (
        <Box
          sx={{
            position: "absolute",
            bottom: "calc(100% - 4px)",
            left: 12,
            right: 12,
            zIndex: 20,
            maxHeight: 240,
            overflow: "auto",
            backgroundColor: theme.palette.secondary.main,
            border: `1px solid ${theme.palette.border.color}`,
            borderRadius: "12px",
            boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
            p: 0.5,
          }}
        >
          {results.length === 0 ? (
            <Typography sx={{ px: 1.5, py: 1, fontSize: 13, color: theme.palette.secondaryText.color }}>
              {records.loaded ? "No matching records" : "Loading records…"}
            </Typography>
          ) : (
            results.map((r, i) => (
              <Box
                key={r.type + r.id}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => selectMention(r)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.25,
                  py: 0.9,
                  borderRadius: "8px",
                  cursor: "pointer",
                  backgroundColor: activeIdx === i ? theme.palette.tertiary.main : "transparent",
                }}
              >
                {r.type === "DEVICE" ? (
                  <DevicesOutlinedIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
                ) : (
                  <BusinessOutlinedIcon sx={{ fontSize: 18, color: theme.palette.secondaryText.color }} />
                )}
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13.5, color: theme.palette.text.color, lineHeight: 1.2 }}>
                    {r.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: theme.palette.secondaryText.color }}>
                    {r.type === "DEVICE" ? "Device" : "Organization"}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      )}

      {/* Context chips */}
      {context.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }}>
          {context.map((c) => (
            <Box
              key={c.type + c.id}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                pl: 1,
                pr: 0.5,
                py: 0.25,
                borderRadius: "999px",
                fontSize: 12,
                backgroundColor: "rgba(59,110,246,0.10)",
                color: accent,
                border: `1px solid ${accent}`,
              }}
            >
              @{c.label}
              <Box
                role="button"
                aria-label={`Remove ${c.label}`}
                onClick={() => removeChip(c.id)}
                sx={{ display: "inline-flex", cursor: "pointer" }}
              >
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          border: `1px solid ${theme.palette.border.color}`,
          borderRadius: 3,
          px: 1,
          py: 0.5,
          transition: "border-color 0.15s ease",
          "&:focus-within": { borderColor: accent },
        }}
      >
        <Tooltip title="Mention a record — type @" arrow>
          <Box
            role="button"
            aria-label="Mention a record"
            onClick={() => {
              setText((t) => t + "@");
              records.load();
              if (inputRef.current) inputRef.current.focus();
            }}
            sx={{ display: "inline-flex", p: 0.75, color: theme.palette.secondaryText.color, cursor: "pointer" }}
          >
            <AddRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
        </Tooltip>

        <InputBase
          inputRef={inputRef}
          value={text}
          onChange={onChange}
          onKeyDown={onKeyDown}
          placeholder="Ask anything, or @ mention a file…"
          multiline
          maxRows={6}
          sx={{ flex: 1, fontSize: 14, color: theme.palette.text.color, py: 0.75 }}
          inputProps={{ "aria-label": "Message the assistant" }}
        />

        {streaming ? (
          <Tooltip title="Stop" arrow>
            <Box
              role="button"
              aria-label="Stop generating"
              onClick={stop}
              sx={{
                display: "grid",
                placeItems: "center",
                width: 30,
                height: 30,
                borderRadius: 2,
                cursor: "pointer",
                backgroundColor: theme.palette.text.color,
                color: theme.palette.tertiary.main,
              }}
            >
              <StopRoundedIcon sx={{ fontSize: 18 }} />
            </Box>
          </Tooltip>
        ) : (
          <Box
            role="button"
            aria-label="Send message"
            aria-disabled={!canSend}
            onClick={submit}
            sx={{
              display: "grid",
              placeItems: "center",
              width: 30,
              height: 30,
              borderRadius: 2,
              cursor: canSend ? "pointer" : "default",
              backgroundColor: canSend ? accent : theme.palette.border.color,
              color: "#fff",
              transition: "background-color 0.15s ease",
            }}
          >
            <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
          </Box>
        )}
      </Box>

      <Typography
        sx={{ mt: 0.75, textAlign: "center", fontSize: 11, color: theme.palette.secondaryText.color }}
      >
        AI-generated content may be inaccurate.
      </Typography>
    </Box>
  );
}
